import { useState } from "react";
import { useTranslation } from "react-i18next";

interface FaqResolveModalProps {
  question: string;
  initialNote?: string | null;
  initialOwner?: string | null;
  saving: boolean;
  onClose: () => void;
  onConfirm: (note: string, owner: string) => void;
}

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400";

export default function FaqResolveModal({
  question,
  initialNote,
  initialOwner,
  saving,
  onClose,
  onConfirm,
}: FaqResolveModalProps) {
  const { t } = useTranslation();
  const [note, setNote] = useState(initialNote ?? "");
  const [owner, setOwner] = useState(initialOwner ?? "");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
      <div className="w-full max-w-lg overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-start justify-between gap-4 border-b border-background-200 px-5 py-4">
          <div className="min-w-0">
            <h3 className="font-heading text-base font-semibold text-foreground-950">{t("helpFeedback.resolveTitle")}</h3>
            <p className="mt-1 truncate text-xs text-foreground-500" title={question}>
              {question}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("helpFeedback.contentCancel")}
            className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div className="flex items-start gap-2.5 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-3">
            <span className="flex h-4 w-4 shrink-0 items-center justify-center text-secondary-700">
              <i className="ri-information-line" />
            </span>
            <p className="text-xs leading-relaxed text-foreground-700">{t("helpFeedback.resolveHint")}</p>
          </div>
          <div>
            <label htmlFor="faq-resolve-note" className="mb-1.5 block text-sm font-medium text-foreground-800">
              {t("helpFeedback.resolveNoteLabel")}
            </label>
            <textarea
              id="faq-resolve-note"
              rows={3}
              maxLength={300}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={t("helpFeedback.resolveNotePlaceholder")}
              className={`${inputClass} resize-none`}
            />
          </div>
          <div>
            <label htmlFor="faq-resolve-owner" className="mb-1.5 block text-sm font-medium text-foreground-800">
              {t("helpFeedback.resolveOwnerLabel")}
            </label>
            <input
              id="faq-resolve-owner"
              type="text"
              value={owner}
              onChange={(event) => setOwner(event.target.value)}
              placeholder={t("helpFeedback.resolveOwnerPlaceholder")}
              className={inputClass}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-background-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-800 transition-colors hover:bg-background-100"
          >
            {t("helpFeedback.contentCancel")}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(note.trim(), owner.trim())}
            disabled={saving}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={saving ? "ri-loader-4-line animate-spin" : "ri-check-line"} />
            </span>
            {saving ? t("helpFeedback.resolveSaving") : t("helpFeedback.resolveConfirm")}
          </button>
        </div>
      </div>
    </div>
  );
}